<template>
  <div :class="`${dark ? 'bg-shade3' : 'bg-gray-50'}`">
    <div class="page-header">
      <shadow-title :text="title" :invert="dark" />
    </div>
    <div class="package-container">
      <button
        class="navigation-button"
        aria-label="previous"
        :disabled="offset == 0"
        @click="previous"
      >
        <s-icon icon="arrow-circle-left" dimension="30" />
      </button>
      <transition-group name="list-complete" tag="div" class="cards">
        <shop-item v-for="item of displayedItems" :key="item.id" :item="item" />
      </transition-group>
      <button
        class="navigation-button"
        aria-label="next"
        :disabled="offset + itemsPerPage >= items.length"
        @click="next"
      >
        <s-icon icon="arrow-circle-right" dimension="30" />
      </button>
    </div>
    <div
      v-if="dotsCount > 1"
      :class="`mt-2 flex justify-center items-center pb-6 ${
        dark ? 'bg-shade3' : ''
      }`"
    >
      <hr class="w-20 border border-gray-400 mr-4 hidden sm:block" />
      <svg
        v-for="i of dotsCount"
        :key="i"
        height="20"
        width="25"
        class="cursor-pointer"
      >
        <circle
          cx="10"
          cy="10"
          r="7"
          :fill="
            i == offset / itemsPerPage + 1
              ? '#8a0900'
              : dark
                ? 'gray'
                : 'lightgray'
          "
          @click="setOffset((i - 1) * itemsPerPage)"
        />
      </svg>
      <hr class="w-20 border border-gray-400 ml-4 hidden sm:block" />
    </div>
  </div>
</template>

<script>
import { useMainStore } from '../store/index';
const ITEMS_PER_BREAKPOINT = {
  xs: 1,
  sm: 2,
  md: 2,
  lg: 2,
  xl: 3,
  '2xl': 4
};

export default {
  props: {
    title: String,
    dark: Boolean,
    animateInterval: {
      type: Number,
      required: false,
      default: 0
    }
  },
  data() {
    return {
      offset: 0,
      itemsPerPage: 4,
      items: [],
      store: useMainStore()
    };
  },
  computed: {
    displayedItems() {
      return this.items.slice(this.offset, this.offset + this.itemsPerPage);
    },
    dotsCount() {
      return Math.ceil(this.items.length / this.itemsPerPage);
    }
  },
  watch: {
    'store.currentBreakpoint': function (currentBreakpoint) {
      this.updateBreakpoint(currentBreakpoint);
    }
  },
  created() {
    if (this.animateInterval > 0) {
      if (!import.meta.client) {
        return;
      }
      this.paginateHandle = setInterval(
        this.autoPaginate,
        this.animateInterval
      );
    }
    $fetch('/api/products/highlights').then(
      function (prods) {
        //todo mockup start
        prods = [
          {
            id: '1',
            label: 'Article 1',
            picture: 'https://www.idmanify.com/',
            price: 99
          },
          {
            id: '2',
            label: 'Article 2',
            picture: 'https://www.idmanify.com/img.jpg',
            price: 129
          },
          {
            id: '3',
            label: 'Article 3',
            picture: 'https://www.idmanify.com/img.jpg',
            price: 39
          },
          {
            id: '4',
            label: 'Article 4',
            picture: 'https://www.idmanify.com/img.jpg',
            price: 179
          }
        ];
        //todo mockup end
        this.items = prods;
      }.bind(this)
    );
  },
  mounted() {
    this.updateBreakpoint(this.store.currentBreakpoint);
  },
  unmounted() {
    if (this.animateInterval > 0) {
      clearInterval(this.paginateHandle);
    }
  },
  methods: {
    updateBreakpoint(breakpoint) {
      const itemsPerPage = ITEMS_PER_BREAKPOINT[breakpoint];
      if (itemsPerPage && itemsPerPage != this.itemsPerPage) {
        this.itemsPerPage = itemsPerPage;
        this.offset = 0;
      }
    },
    pauseAnimation() {
      clearInterval(this.paginateHandle);
    },
    resumeAnimation() {
      if (!import.meta.client) {
        return;
      }
      clearInterval(this.paginateHandle);
      if (this.animateInterval > 0) {
        this.paginateHandle = setInterval(
          this.autoPaginate,
          this.animateInterval
        );
      }
    },
    setOffset(offset) {
      if (!import.meta.client) {
        return;
      }
      clearInterval(this.paginateHandle);
      this.offset = offset;
      if (this.animateInterval > 0) {
        this.paginateHandle = setInterval(
          this.autoPaginate,
          this.animateInterval
        );
      }
    },
    autoPaginate() {
      if (this.offset + this.itemsPerPage < this.items.length) {
        this.offset += this.itemsPerPage;
      } else {
        this.offset = 0;
      }
    },
    next() {
      if (!import.meta.client) {
        return;
      }
      if (this.offset + this.itemsPerPage < this.items.length) {
        if (this.animateInterval > 0) {
          clearInterval(this.paginateHandle);
          this.paginateHandle = setInterval(
            this.autoPaginate,
            this.animateInterval
          );
        }
        this.offset += this.itemsPerPage;
      }
    },
    previous() {
      if (!import.meta.client) {
        return;
      }
      if (this.offset > 0) {
        if (this.animateInterval > 0) {
          clearInterval(this.paginateHandle);
          this.paginateHandle = setInterval(
            this.autoPaginate,
            this.animateInterval
          );
        }
        this.offset -= this.itemsPerPage;
      }
    }
  }
};
</script>

<style scoped>
@reference "@/assets/css/tailwind.css";
.page-header {
  @apply flex justify-center pt-6 pb-8;
}
.navigation-button {
  @apply relative top-60 text-orange-700 hover:text-orange-600 rounded disabled:invisible;
}
.package-container {
  @apply flex justify-between items-start flex-nowrap pb-6 px-2 sm:px-4 xl:px-6;
}
.cards {
  @apply grow flex justify-evenly items-stretch flex-nowrap sm:px-2 xl:px-8;
}
</style>
